import CloseIcon from "@mui/icons-material/Close";
import { Box, Collapse, Fade, IconButton, Portal } from "@mui/material";
import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/**
 * Props for {@link CategoryPanel}, the collapsible floating area mounted under
 * the discovery navigation tabs. See `docs/browse-by-category-implementation-guide.md`.
 */
export interface CategoryPanelProps {
  /** Whether the panel is expanded. Controls the MUI Collapse and backdrop. */
  open: boolean;
  /**
   * Called when the user dismisses the panel (backdrop click or close icon).
   * When omitted, the close icon is hidden and the panel can only be closed
   * by flipping `open` from the parent.
   */
  onClose?: () => void;
  /** Panel body — typically the category-specific `CovCardOverlay` cards. */
  children: ReactNode;
  /** When true, renders the panel inline (in-flow) instead of as a fixed overlay. */
  inline?: boolean;
}

const PANEL_TRANSITION =
  "opacity 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)";

export const CategoryPanel = ({
  open,
  onClose,
  children,
  inline,
}: CategoryPanelProps) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [navBottom, setNavBottom] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  useLayoutEffect(() => {
    if (!mounted || !anchorRef.current) return;

    let rafId: number | null = null;
    const update = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (anchorRef.current) {
          const top = anchorRef.current.getBoundingClientRect().top;
          setNavBottom((prev) => (prev === top ? prev : top));
        }
      });
    };
    update();

    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [mounted]);

  if (inline) {
    return (
      <Collapse in={open} timeout={300} unmountOnExit>
        <Box
          sx={{
            backgroundColor: "var(--alias-background-white, #fff)",
            borderBottom:
              "1px solid var(--alias-stroke-on-white-default, #d9d9d9)",
          }}
        >
          {onClose && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "var(--spacing8, 8px) var(--spacing16, 16px) 0",
              }}
            >
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          )}
          {children}
        </Box>
      </Collapse>
    );
  }

  return (
    <>
      {mounted && (
        <Portal>
          <Fade in={open} timeout={250} onExited={() => setMounted(false)}>
            <Box
              onClick={onClose}
              sx={{
                position: "fixed",
                top: Math.max(navBottom, 0),
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 29,
                backgroundColor: "rgba(0, 0, 0, 0.4)",
              }}
            />
          </Fade>
        </Portal>
      )}
      <Box ref={anchorRef} sx={{ position: "relative" }}>
        {mounted && (
          <Portal>
            <Box
              sx={{
                position: "fixed",
                top: Math.max(navBottom, 0),
                left: 0,
                right: 0,
                zIndex: 30,
                maxHeight: `calc(100vh - ${Math.max(navBottom, 0)}px)`,
                overflowY: "auto",
                backgroundColor: "var(--alias-background-white, #fff)",
                boxShadow:
                  "0 24px 48px rgba(0,0,0,0.12), 0 12px 24px rgba(0,0,0,0.08)",
                transformOrigin: "top",
                transition: PANEL_TRANSITION,
                opacity: open ? 1 : 0,
                transform: open
                  ? "translateY(0) scaleY(1)"
                  : "translateY(-12px) scaleY(0.96)",
                pointerEvents: open ? "auto" : "none",
              }}
            >
              {onClose && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    padding: "var(--spacing8, 8px) var(--spacing16, 16px) 0",
                  }}
                >
                  <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                  </IconButton>
                </Box>
              )}
              {children}
            </Box>
          </Portal>
        )}
      </Box>
    </>
  );
};
